import React, {  useMemo, useState, useEffect } from 'react'
import classes from './ProductReviews.module.scss';
import ReviewStars from '@/components/Review/ReviewStars/ReviewStars';
import useProductsReviews from '@/hooks/useProductsReviews';
import FullStarIcon from '@/svgs/full-star.svg';
import { useMediaQuery } from 'react-responsive';
import CustomDropdown from './CustomDropdown';

const STAR_OPTIONS = [
  { value: '', label: 'All Stars' },
  { value: 5, label: '5 Stars' },
  { value: 4, label: '4 Stars' },
  { value: 3, label: '3 Stars' },
  { value: 2, label: '2 Stars' },
  { value: 1, label: '1 Star' },
];

const SORT_OPTIONS = [
  { value: 'mostRecent', label: 'Most Recent' },
  { value: 'allStars', label: 'Rating' },
];

const ProductReviews = React.forwardRef(({ fields }, ref) => {
  const [sortBy, setSortBy] = useState('mostRecent');
  const [filterByStar, setFilterByStar] = useState(null);
  const [loadedReviews, setLoadedReviews] = useState([]);
  const [isReviewsVisible, setIsReviewsVisible] = useState(false); // State to toggle reviews visibility
  const [expandedReviews, setExpandedReviews] = useState({});
  const [visibleReviewsCount, setVisibleReviewsCount] = useState(3);
  const reviewsPerPage = 1;

  const reviews = useProductsReviews(
    fields?.products?.map((product) => product?.handle?.current)
  );

  const isDesktop = useMediaQuery({ query: '(min-width: 768px)' }); // Check if it's desktop

  // Set initial visibility based on the screen size
  useEffect(() => {
    if (isDesktop) {
      setIsReviewsVisible(true); // Show reviews by default on desktop
    }
  }, [isDesktop]);

  useEffect(() => {
    const handleHashChange = () => {
      setIsReviewsVisible(typeof window !== 'undefined' && window.location.hash === '#reviews');
    };

    if (typeof window !== 'undefined') {  
      window.addEventListener('hashchange', handleHashChange);  // Listen for hash changes
    }
    return () => {
      if (typeof window !== 'undefined') {
        window.removeEventListener('hashchange', handleHashChange); // Cleanup
      }
    };
  }, []);

  const toggleReviewsVisibility = () => {
    setIsReviewsVisible((prev) => !prev);
  };

  const toggleReadMore = (reviewId) => {
    setExpandedReviews((prev) => ({
      ...prev,
      [reviewId]: !prev[reviewId],
    }));
  };

  useEffect(() => {
    if (reviews.length && loadedReviews.length === 0) {
      setLoadedReviews(reviews.slice(0, reviewsPerPage));
    }
  }, [reviews, loadedReviews.length, reviewsPerPage]);

  const reviewsToShow = useMemo(() => {  // Calculate filtered and sorted reviews
    let filteredReviews = filterByStar
        ? reviews.filter((review) => review.rating === filterByStar)
        : reviews;

    if (sortBy === 'mostRecent') {
        filteredReviews = filteredReviews.sort((a, b) => new Date(b.created) - new Date(a.created));
    } else if (sortBy === 'allStars') {
        filteredReviews = filteredReviews.sort((a, b) => b.rating - a.rating);
    }

    return filteredReviews;
  }, [sortBy, filterByStar, reviews]);

  const reviewsSummary = useMemo(() => {
    if (!reviews.length) return {};

    const totalCount = reviews.length;
    const totalRating = reviews.reduce((sum, review) => sum + review.rating, 0);
    const averageRating = parseFloat((totalRating / totalCount).toFixed(1));

    const ratingsDistribution = [5, 4, 3, 2, 1].reduce((acc, rating) => {
      acc[rating] = 0;
      return acc;
    }, {});

    reviews.forEach(({ rating }) => {
      ratingsDistribution[rating]++;
    });

    for (const rating in ratingsDistribution) {
      ratingsDistribution[rating] = Math.round(
        (ratingsDistribution[rating] / totalCount) * 100
      );
    }

    return { totalCount, averageRating, ratingsDistribution };
  }, [reviews]);

  const displayedReviews = reviewsToShow.slice(0, visibleReviewsCount);
  const handleShowMore = () => {
    setVisibleReviewsCount(prevCount =>
      prevCount + 3 > reviewsToShow.length ? reviewsToShow.length : prevCount + 3
    );
  };

  if (!Object.keys(reviewsSummary).length) return null;

  return (
    <div ref={ref} className={`${classes['product__row']} container`}>
      <div className={classes.reviewsContainer}>
        <h1 className={classes.title}>Reviews</h1>
        <div className={classes.filters}>
          <div className={classes.sortBy}>
            <CustomDropdown
              options={SORT_OPTIONS}
              selectedValue={SORT_OPTIONS.find(option => option.value === sortBy)?.label}
              onSelect={(value) => setSortBy(value)}
            />
            <CustomDropdown
              options={STAR_OPTIONS}
              selectedValue={
                filterByStar === null
                  ? "All Stars"
                  : STAR_OPTIONS.find(option => option.value === filterByStar)?.label
              }
              onSelect={(value) => setFilterByStar(value ? parseInt(value, 10) : null)}
            />
          </div>
        </div>

        <div className={classes.summary}>
          <ReviewStars rating={reviewsSummary.averageRating} maxWidth={120} />
          <span className={classes.averageRating}>
            {reviewsSummary.averageRating} out of 5 stars
          </span>
          <span className={classes.totalReviews}>
            Based on {reviewsSummary.totalCount} reviews
          </span>
        </div>

        <div className={classes.ratingDistribution}>
          {[5, 4, 3, 2, 1].map((rating) => (
            <div key={rating} className={classes.ratingBar}>
              <span className={classes.ratingLabel}>
                {rating} <FullStarIcon className={classes['star-icon']} />
              </span>
              <div className={classes.bar}>
                <div
                  className={classes.fill}
                  style={{ width: `${reviewsSummary.ratingsDistribution[rating] || 0}%` }}
                />
              </div>
              <span className={classes.percentage}>
                {reviewsSummary.ratingsDistribution[rating] || 0}%
              </span>
            </div>
          ))}
        </div>

        {!isReviewsVisible && (
          <a
            className={classes.readReviews}
            onClick={toggleReviewsVisibility}
          >
            Read Reviews
          </a>
        )}

        {isReviewsVisible && (
          <div className={classes.reviewsList}>
            {displayedReviews.map((review) => {
              const isExpanded = expandedReviews[review.id];
              const maxLength = typeof window !== 'undefined' && window.innerWidth <= 768 ? 130 : 500;
              const displayText = isExpanded
                ? review.body
                : `${review.body.slice(0, maxLength)}${review.body.length > maxLength ? '...' : ''}`;

              return (
                <div key={review.id} className={classes.reviewItem}>
                  <div className={classes.reviewHeader}>
                    <span className={classes.author}>
                      {`${review.author.split(' ')[0]} ${review.author.split(' ')[1]?.charAt(0)}.`}
                      <span className={classes.verified}>VERIFIED PURCHASE</span>
                    </span>
                    <ReviewStars rating={review.rating} maxWidth={100} />
                  </div>
                  <p className={classes.reviewBody}>
                    {displayText}
                    {review.body.length > maxLength && (
                      <span
                        className={classes['read-more-review']}
                        onClick={() => toggleReadMore(review.id)}
                      >
                        {isExpanded ? 'Read less' : 'Read more'}
                      </span>
                    )}
                  </p>
                  <div className={classes.reviewFooter}>
                    <span className={classes.date}>
                      {new Date(review.dateCreated).toLocaleDateString()}
                    </span>
                    {review.helpfulCount && (
                      <span className={classes.helpful}>
                        Helpful ({review.helpfulCount})
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
            {displayedReviews.length < reviewsToShow.length && (
              <a className={classes.showMoreReviews} onClick={handleShowMore}>
                  More Reviews
              </a>
            )}
          </div>
        )}
      </div>
    </div>
  );
});

export default ProductReviews;
